import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const ServicesOverview = () => {
  const services = [
    {
      id: 1,
      title: "Software Development",
      description: "Custom enterprise software solutions built with cutting-edge technologies and scalable architectures.",
      icon: "Code",
      color: "primary",
      features: ["Full-Stack Development", "Cloud Architecture", "API Integration"]
    },
    {
      id: 2,
      title: "AI Models",
      description: "Advanced machine learning models and neural networks tailored to your specific business requirements.",
      icon: "Brain",
      color: "secondary",
      features: ["Custom ML Models", "Deep Learning", "Model Optimization"]
    },
    {
      id: 3,
      title: "Analytics",
      description: "Data-driven insights and business intelligence solutions that transform raw data into actionable strategies.",
      icon: "BarChart3",
      color: "accent",
      features: ["Business Intelligence", "Predictive Analytics", "Data Visualization"]
    },
    {
      id: 4,
      title: "Cybersecurity",
      description: "Comprehensive security solutions protecting your digital assets with advanced threat detection and prevention.",
      icon: "Shield",
      color: "warning",
      features: ["Threat Detection", "Security Audits", "Compliance Management"]
    },
    {
      id: 5,
      title: "AI Engineering",
      description: "End-to-end AI implementation services from concept to deployment with ongoing optimization and support.",
      icon: "Cpu",
      color: "success",
      features: ["AI Strategy", "Implementation", "Optimization"]
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      primary: "bg-primary/10 text-primary border-primary/20",
      secondary: "bg-secondary/10 text-secondary border-secondary/20",
      accent: "bg-accent/10 text-accent border-accent/20",
      warning: "bg-warning/10 text-warning border-warning/20",
      success: "bg-success/10 text-success border-success/20"
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Briefcase" size={16} />
            <span>Our Expertise</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Comprehensive AI & Technology Solutions
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From software development to advanced AI implementation, we deliver end-to-end solutions that drive digital transformation and business growth.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {services.map((service) => (
            <Link
              key={service.id}
              to="/individual-service-detail"
              className="group bg-white rounded-xl border border-border hover:border-primary/30 shadow-soft hover:shadow-elevation transition-all duration-300 p-8 hover:-translate-y-1"
            >
              {/* Icon */}
              <div className={`w-16 h-16 rounded-xl ${getColorClasses(service.color)} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <Icon name={service.icon} size={28} />
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                {service.title}
              </h3>
              
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {service.description}
              </p>

              {/* Features */}
              <div className="space-y-2 mb-6">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2 text-sm">
                    <Icon name="Check" size={14} className="text-success" />
                    <span className="text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="flex items-center space-x-2 text-primary font-medium group-hover:space-x-3 transition-all">
                <span>Learn More</span>
                <Icon name="ArrowRight" size={16} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <Link
            to="/services-overview"
            className="inline-flex items-center space-x-2 bg-primary text-white px-8 py-4 rounded-lg font-semibold hover:bg-secondary transition-colors shadow-soft hover:shadow-elevation"
          >
            <span>Explore All Services</span>
            <Icon name="ArrowRight" size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesOverview;